import React, { useState, useRef, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaArrowLeft,
  FaSave,
  FaExclamationCircle,
} from "react-icons/fa";
import CategoryForm from "../components/CategoryForm";
import QuestionForm from "../components/QuestionForm";
import FormPreview from "../components/FormPreview";
import { INITIAL_TEMPLATE } from "../_constant";
import {
  useCreateItem,
  useUpdateItem,
} from "../../_helper/actions/mutation/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import Select from "react-select";
import { toast } from "react-toastify";
import CuSpinner from "../../common_component/component/CuSpinner";

const QuestionnaireBuilder = ({
  template,
  setTemplate,
  doctors,
  clinic_id,
}) => {
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [error, setError] = useState("");
  const editFormRef = useRef(null);

  // Map doctors to options for the dropdown
  const doctorOptions = doctors?.map((doctor) => ({
    value: doctor.id,
    label: `${doctor.first_name} ${doctor.last_name}`,
  }));

  const handleAssignDoctor = (selectedDoctor) => {
    setTemplate((prev) => ({
      ...prev,
      doctor_id: selectedDoctor ? selectedDoctor.value : null,
    }));
  };

  // Get the selected doctor option from doctor_id
  const selectedDoctor = doctorOptions?.find(
    (option) => option.value === template.doctor_id
  );

  // Scroll to the edit form when it's opened
  useEffect(() => {
    if (editFormRef.current) {
      editFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [editingCategoryId, editingQuestion]);

  const validateTemplateName = () => {
    if (!template.template_name.trim()) {
      setError("Template name is required.");
      return false;
    }
    setError("");
    return true;
  };

  const handleAddCategory = (category) => {
    if (!validateTemplateName()) return;

    setTemplate((prev) => ({
      ...prev,
      categories: [...prev.categories, { ...category, questions: [] }],
    }));
    setEditingCategoryId(null);
  };

  const handleEditCategory = (updatedCategory) => {
    if (!validateTemplateName()) return;

    setTemplate((prev) => ({
      ...prev,
      categories: prev?.categories?.map((cat) =>
        cat.category_id === updatedCategory.category_id ? updatedCategory : cat
      ),
    }));
    setEditingCategoryId(null);
  };

  const handleAddQuestion = (categoryId, question) => {
    if (!validateTemplateName()) return;

    setTemplate((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.category_id === categoryId
          ? {
              ...cat,
              questions: [
                ...cat.questions,
                { ...question, question_id: Date.now().toString() },
              ],
            }
          : cat
      ),
    }));
    setEditingQuestion(null);
  };

  const handleEditQuestion = (categoryId, updatedQuestion) => {
    if (!validateTemplateName()) return;

    setTemplate((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.category_id === categoryId
          ? {
              ...cat,
              questions: cat.questions.map((q) =>
                q.question_id === updatedQuestion.question_id
                  ? updatedQuestion
                  : q
              ),
            }
          : cat
      ),
    }));
    setEditingQuestion(null);
  };

  const handleDeleteCategory = (categoryId) => {
    setTemplate((prev) => ({
      ...prev,
      categories: prev.categories.filter(
        (cat) => cat.category_id !== categoryId
      ),
    }));
  };

  const handleDeleteQuestion = (categoryId, questionId) => {
    setTemplate((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.category_id === categoryId
          ? {
              ...cat,
              questions: cat.questions.filter(
                (q) => q.question_id !== questionId
              ),
            }
          : cat
      ),
    }));
  };

  const createMutation = useCreateItem(ApiEndPoint.CONSULTATION);
  const updateMutation = useUpdateItem(ApiEndPoint.CONSULTATION);

  // console.log("Template", template);
  const handleSaveTemplate = () => {
    if (!validateTemplateName()) return;
    if (template?.id) {
      const payload = {
        ...template,
        clinic_id: clinic_id,
        action: "patchPreconsultationTemplete",
      };
      updateMutation.mutate(
        { data: payload },
        {
          onSuccess: () => {
            toast.success("Templete Updated Succesfully");
          },
          onError: () => {
            toast.error("Failed to update templete");
          },
        }
      );
    } else {
      createMutation.mutate(
        {
          ...template,
          clinic_id: clinic_id,
          action: "postPreconsultationTemplete",
        },
        {
          onSuccess: () => {
            toast.success("Templete Created Succesfully");
          },
          onError: () => {
            toast.error("Failed to create templete");
          },
        }
      );
    }

    // Save logic goes here
  };

  const togglePreviewMode = () => setPreviewMode((prev) => !prev);

  const onDragEnd = (result, categoryId) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    setTemplate((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.category_id === categoryId
          ? {
              ...cat,
              questions: reorderArray(
                cat.questions,
                sourceIndex,
                destinationIndex
              ),
            }
          : cat
      ),
    }));
  };

  const reorderArray = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-blue-600">
          Questionnaire Builder
        </h1>
        <button
          onClick={handleSaveTemplate}
          className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 flex items-center gap-2"
        >
          {updateMutation.isLoading || createMutation.isLoading ? (
            <CuSpinner />
          ) : (
            <>
              <FaSave /> Save Template
            </>
          )}
        </button>
      </div>

      {!previewMode ? (
        <>
          <div className="bg-white p-4 rounded shadow mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Template Name
            </label>
            <input
              type="text"
              value={template?.template_name}
              onChange={(e) =>
                setTemplate((prev) => ({
                  ...prev,
                  template_name: e.target.value,
                }))
              }
              placeholder="Enter Template Name"
              className={`border p-2 w-full rounded focus:outline-none focus:ring-2 ${
                error
                  ? "border-red-500 focus:ring-red-400"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            {error && (
              <p className="text-red-500 flex items-center mt-2">
                <FaExclamationCircle className="mr-2" />
                {error}
              </p>
            )}
            {/* Doctor Assignment Dropdown */}
            <div className="mt-4">
              <label className="block text-gray-700 font-medium mb-2">
                Assign Doctor
              </label>
              <Select
                options={doctorOptions}
                onChange={handleAssignDoctor}
                value={selectedDoctor || null}
                placeholder="Select a Doctor"
                className="w-full"
              />
            </div>
          </div>

          <button
            onClick={() => setEditingCategoryId("new")}
            className="bg-green-500 text-white px-4 py-2 mb-6 rounded shadow hover:bg-green-600 flex items-center gap-2"
          >
            <FaPlus /> Add Category
          </button>

          {template?.categories?.map((category) => (
            <div
              key={category.category_id}
              className="mb-6 transition-transform duration-300 ease-in-out"
            >
              <div className="flex justify-between items-center mb-2 bg-gray-100 p-4 rounded">
                <h3 className="text-xl font-semibold text-gray-700">
                  {category?.category_name}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setEditingCategoryId(
                        editingCategoryId === category.category_id
                          ? null
                          : category?.category_id
                      )
                    }
                    className="bg-blue-500 text-white px-3 py-1 rounded shadow hover:bg-blue-600 flex items-center gap-2"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category?.category_id)}
                    className="bg-red-500 text-white px-3 py-1 rounded shadow hover:bg-red-600 flex items-center gap-2"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              {editingCategoryId === category?.category_id && (
                <div
                  ref={editFormRef}
                  className="p-4 bg-white rounded shadow border border-gray-200 animate-fade-in"
                >
                  <CategoryForm
                    category={category}
                    onSave={handleEditCategory}
                    onCancel={() => setEditingCategoryId(null)}
                  />
                </div>
              )}

              <DragDropContext
                onDragEnd={(result) => onDragEnd(result, category.category_id)}
              >
                <Droppable droppableId={category?.category_id}>
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="p-4 bg-white rounded shadow"
                    >
                      {category?.questions.map((question, index) => (
                        <Draggable
                          key={question?.question_id}
                          draggableId={question?.question_id}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="p-4 border rounded mb-2 flex justify-between items-center bg-gray-50 animate-slide-in"
                            >
                              <div>
                                <strong>{index + 1}.</strong> {question.text} (
                                {question?.type})
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() =>
                                    setEditingQuestion({
                                      ...question,
                                      categoryId: category.category_id,
                                    })
                                  }
                                  className="bg-blue-500 text-white px-3 py-1 rounded shadow hover:bg-blue-600 flex items-center gap-2"
                                >
                                  <FaEdit /> Edit
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteQuestion(
                                      category?.category_id,
                                      question?.question_id
                                    )
                                  }
                                  className="bg-red-500 text-white px-3 py-1 rounded shadow hover:bg-red-600 flex items-center gap-2"
                                >
                                  <FaTrash /> Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {editingQuestion &&
                editingQuestion?.categoryId === category?.category_id && (
                  <div
                    ref={editFormRef}
                    className="p-4 bg-white rounded shadow border border-gray-200 animate-fade-in"
                  >
                    <QuestionForm
                      question={editingQuestion}
                      onSave={(updatedQuestion) =>
                        editingQuestion.question_id
                          ? handleEditQuestion(
                              category.category_id,
                              updatedQuestion
                            )
                          : handleAddQuestion(
                              category.category_id,
                              updatedQuestion
                            )
                      }
                      onCancel={() => setEditingQuestion(null)}
                    />
                  </div>
                )}

              <button
                onClick={() =>
                  setEditingQuestion({ categoryId: category?.category_id })
                }
                className="bg-green-500 text-white px-4 py-2 mt-4 rounded shadow hover:bg-green-600 flex items-center gap-2"
              >
                <FaPlus /> Add Question
              </button>
            </div>
          ))}

          {editingCategoryId === "new" && (
            <div
              ref={editFormRef}
              className="p-4 bg-white rounded shadow border border-gray-200 animate-fade-in"
            >
              <CategoryForm
                onSave={handleAddCategory}
                onCancel={() => setEditingCategoryId(null)}
              />
            </div>
          )}

          <button
            onClick={togglePreviewMode}
            className="bg-yellow-500 text-white px-4 py-2 mt-6 rounded mx-auto shadow hover:bg-yellow-600 flex items-center gap-2"
          >
            <FaEye /> Preview Form
          </button>
        </>
      ) : (
        <>
          <button
            onClick={togglePreviewMode}
            className="bg-gray-500 text-white px-4 py-2 mt-6 rounded shadow hover:bg-gray-600 flex items-center gap-2"
          >
            <FaArrowLeft /> Back to Builder
          </button>
          <FormPreview template={template} />
        </>
      )}
    </div>
  );
};

export default QuestionnaireBuilder;
